(() => {
  const initialize = (root) => {
    if (root.dataset.bookingReady) return;
    root.dataset.bookingReady = 'true';
    const form = root.querySelector('[data-booking-form]');
    const panels = [...form.querySelectorAll('[data-step]')];
    const progress = [...form.querySelectorAll('[data-progress]')];
    const status = form.querySelector('[data-status]');
    const next = form.querySelector('[data-next]');
    const back = form.querySelector('[data-back]');
    const recovery = form.querySelector('[data-cart-recovery]');
    const success = root.querySelector('[data-booking-success]');
    const dialog = root.querySelector('[data-booking-dialog]');
    let bookingTrigger;
    const catalog = JSON.parse(root.querySelector('[data-rental-catalog]').textContent);
    const money = new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: catalog.currency, maximumFractionDigits: 2 });
    const cartUrl = form.dataset.cartUrl || '/cart';
    const protectionPrice = catalog.protection.price;
    let step = 0;
    let busy = false;
    let uncertain = false;

    const selection = () => {
      const data = new FormData(form);
      const sport = data.get('sport');
      const age = data.get('age');
      const name = String(data.get('renter') || '').trim();
      const protectedRental = data.get('protection') === 'on';
      const variant = catalog[sport]?.[age];
      const price = variant?.price || 0;
      return {
        sport, age, name, protectedRental, price,
        id: variant?.id,
        available: variant?.available === true,
        title: `${age === 'youth' ? 'Youth' : 'Adult'} ${sport} package`,
        ageLabel: age === 'youth' ? 'Ages 9 and under' : 'Ages 10+',
        total: price + (protectedRental ? protectionPrice : 0)
      };
    };

    const renderAgeOptions = () => {
      const { sport } = selection();
      ['adult', 'youth'].forEach(age => {
        const variant = catalog[sport]?.[age];
        const input = form.querySelector(`[name="age"][value="${age}"]`);
        input.disabled = !variant?.available;
        if (input.disabled) input.checked = false;
        form.querySelector(`[data-age-price="${age}"]`).textContent = variant?.available
          ? `${money.format(variant.price / 100)} for the season`
          : 'Currently unavailable';
      });
    };

    const renderReview = () => {
      const rental = selection();
      const values = {
        name: rental.name,
        package: `${rental.title} / ${rental.ageLabel}`,
        price: money.format(rental.price / 100),
        protection: rental.protectedRental ? money.format(protectionPrice / 100) : 'Not added',
        total: money.format(rental.total / 100)
      };
      Object.entries(values).forEach(([key, value]) => {
        form.querySelector(`[data-review-${key}]`).textContent = value;
      });
    };

    const showStep = (index, focus = true) => {
      step = index;
      panels.forEach((panel, i) => { panel.hidden = i !== step; });
      progress.forEach((item, i) => {
        item.classList.toggle('is-complete', i < step);
        if (i === step) item.setAttribute('aria-current', 'step');
        else item.removeAttribute('aria-current');
      });
      back.hidden = step === 0;
      next.textContent = step === 3 ? 'Add rental to cart' : 'Continue';
      next.disabled = busy || uncertain;
      status.textContent = '';
      recovery.hidden = !uncertain;
      if (step === 1) renderAgeOptions();
      if (step === 3) renderReview();
      if (focus) panels[step].querySelector('h2').focus();
    };

    const validate = (index) => {
      const rental = selection();
      let field;
      let message;
      if (index === 0 && !rental.sport) {
        field = form.querySelector('[name="sport"]');
        message = 'Choose skis or snowboard to continue.';
      } else if (index === 0 && !Object.values(catalog[rental.sport] || {}).some(variant => variant.available)) {
        field = form.querySelector('[name="sport"]');
        message = 'This equipment is currently unavailable online. Please choose another option or call (719) 465-2450.';
      } else if (index === 1 && !rental.name) {
        field = form.querySelector('[name="renter"]');
        message = "Enter the renter's full name.";
      } else if (index === 1 && !rental.age) {
        field = form.querySelector('[name="age"]');
        message = "Choose the renter's age group.";
      } else if (index === 1 && !rental.available) {
        field = form.querySelector('[name="age"]');
        message = 'This package is currently unavailable. Please choose another option.';
      } else if (index === 2 && rental.protectedRental && !catalog.protection.available) {
        field = form.querySelector('[name="protection"]');
        message = 'Protection is currently unavailable online. Please continue without it or call us.';
      }
      if (!field) return true;
      if (step !== index) showStep(index);
      status.textContent = message;
      field.focus();
      return false;
    };

    const showSuccess = (rental) => {
      busy = false;
      uncertain = false;
      form.hidden = true;
      success.hidden = false;
      root.querySelector('[data-success-summary]').textContent = `${rental.name}: ${rental.title}${rental.protectedRental ? ' with damage protection' : ''} - ${money.format(rental.total / 100)} before tax.`;
      success.querySelector('h2').focus();
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    };

    const addRental = async () => {
      const rental = selection();
      if (!validate(0) || !validate(1) || !validate(2)) return;
      if (!Number.isSafeInteger(rental.id) || rental.id <= 0) {
        status.textContent = 'This package is unavailable. Please call (719) 465-2450.';
        return;
      }
      busy = true;
      next.disabled = true;
      back.disabled = true;
      next.textContent = 'Adding...';
      status.textContent = '';
      // A shared reference keeps each renter and their optional protection together.
      const reference = crypto.randomUUID();
      const properties = {
        'Renter name': rental.name,
        'Rental package': rental.title,
        'Age group': rental.ageLabel,
        'Rental pickup': 'In-store fitting required before gear leaves Gearonimo',
        '__Rental booking': reference
      };
      const items = [{ id: rental.id, quantity: 1, properties }];
      if (rental.protectedRental) {
        items.push({ id: catalog.protection.id, quantity: 1, properties: {
          'For rental': rental.title,
          'Renter name': rental.name,
          '__Rental booking': reference
        } });
      }
      try {
        const response = await fetch(`${cartUrl}/add.js`, {
          method: 'POST', headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ items })
        });
        if (!response.ok) throw new Error('Cart addition rejected');
        await response.json();
        showSuccess(rental);
      } catch (error) {
        // A dropped response can still mean the cart was updated. Check before allowing a retry.
        try {
          const response = await fetch(`${cartUrl}.js`, { headers: { 'Accept': 'application/json' }, cache: 'no-store' });
          if (!response.ok) throw new Error('Cart check failed');
          const cart = await response.json();
          if (!Array.isArray(cart.items)) throw new Error('Invalid cart');
          const matching = cart.items.filter(item => item.properties?.['__Rental booking'] === reference);
          const complete = items.every(expected => matching.some(item => Number(item.variant_id || item.id) === expected.id && item.quantity === 1));
          if (complete) { showSuccess(rental); return; }
          uncertain = matching.length > 0;
          status.textContent = uncertain
            ? 'Only part of this rental was added. Review your cart before continuing.'
            : 'This rental could not be added. Please try again or call (719) 465-2450.';
        } catch (checkError) {
          uncertain = true;
          status.textContent = 'We could not confirm your cart. Review it before adding this rental again.';
        }
        recovery.hidden = false;
      } finally {
        busy = false;
        next.disabled = uncertain;
        back.disabled = false;
        next.textContent = 'Add rental to cart';
      }
    };

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      if (busy || uncertain || !validate(step)) return;
      if (step < 3) showStep(step + 1);
      else addRental();
    });
    back.addEventListener('click', () => { if (!busy && step > 0) showStep(step - 1); });
    root.querySelector('[data-another-renter]').addEventListener('click', () => {
      form.reset();
      success.hidden = true;
      form.hidden = false;
      uncertain = false;
      showStep(0);
    });
    form.hidden = false;
    showStep(0, false);
    root.querySelectorAll('[data-start-booking]').forEach(button => {
      button.addEventListener('click', () => {
        bookingTrigger = button;
        if (button.dataset.rentalSport && !busy && !uncertain) {
          if (form.hidden) form.reset();
          success.hidden = true;
          form.hidden = false;
          form.querySelector('[name="sport"][value="' + button.dataset.rentalSport + '"]').checked = true;
          renderAgeOptions();
          const ageInput = form.querySelector('[name="age"][value="' + button.dataset.rentalAge + '"]');
          if (!ageInput.disabled) ageInput.checked = true;
          showStep(1, false);
        }
        if (!dialog.open) dialog.showModal();
        const heading = form.hidden ? success.querySelector('h2') : panels[step].querySelector('h2');
        heading.focus();
      });
    });
    root.querySelector('[data-close-booking]').addEventListener('click', () => dialog.close());
    dialog.addEventListener('close', () => bookingTrigger?.focus({ preventScroll: true }));
  };
  const initializeAll = () => document.querySelectorAll('[data-rental-booking]').forEach(initialize);
  initializeAll();
  document.addEventListener('shopify:section:load', initializeAll);
})();
